<script setup lang="ts">
interface Props {
  count: number
  journeyStage: string
  attritionRisk: string
  opacity: number
  grandTotal: number
}

const props = defineProps<Props>()

// Format number with Turkish locale
const formatNumber = (num: number) => {
  return new Intl.NumberFormat("tr-TR").format(num)
}

// Get percentage of total
const getPercentage = (count: number) => {
  if (props.grandTotal === 0) return "0"
  return ((count / props.grandTotal) * 100).toFixed(1)
}

// Get segment title (e.g., "Healthy rookies", "Sliding whales")
const getSegmentTitle = () => {
  return `${props.attritionRisk} ${props.journeyStage.toLowerCase()}s`
}

// Get segment description
const getSegmentDescription = () => {
  const descriptions: Record<string, Record<string, string>> = {
    Rookie: {
      Healthy: "New customers, fewer than 3 orders, active recently",
      Sliding: "New customers, fewer than 3 orders, haven't visited for 1-3 months",
      Churned: "New customers, fewer than 3 orders, inactive for 3+ months"
    },
    Occasional: {
      Healthy: "Occasional customers with 3+ orders, active recently",
      Sliding: "Occasional customers with 3+ orders, haven't visited for 1-3 months",
      Churned: "Occasional customers with 3+ orders, inactive for 3+ months"
    },
    Loyalist: {
      Healthy: "Frequent buyers who order monthly or more, active recently",
      Sliding: "Frequent buyers who order monthly or more, haven't visited for 1-3 months",
      Churned: "Frequent buyers who order monthly or more, inactive for 3+ months"
    },
    Whale: {
      Healthy: "High spenders who pay 3x, active recently",
      Sliding: "High spenders who pay 3x, haven't visited for 1-3 months",
      Churned: "High spenders who pay 3x, inactive for 3+ months"
    }
  }
  return descriptions[props.journeyStage]?.[props.attritionRisk] || ""
}
</script>

<template>
  <UPopover mode="hover" :open-delay="200">
    <td
      class="text-center p-4 text-xl font-bold tabular-nums transition-all duration-200 hover:ring-2 hover:ring-inset hover:ring-gray-400 dark:hover:ring-gray-500 cursor-pointer relative after:absolute after:inset-0 after:bg-white/0 hover:after:bg-white/20 after:transition-colors after:duration-200 after:pointer-events-none"
      :style="{ backgroundColor: `rgba(107, 114, 128, ${opacity})` }"
    >
      <span class="relative z-10">{{ formatNumber(count) }}</span>
    </td>
    <template #content>
      <div class="p-3 w-52">
        <h4 class="font-semibold text-sm mb-1.5 capitalize">{{ getSegmentTitle() }}</h4>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2.5 leading-relaxed">{{ getSegmentDescription() }}</p>
        <div class="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1.5">
          <div class="flex justify-between items-baseline">
            <span class="text-xs text-gray-500">Count</span>
            <span class="text-sm font-semibold tabular-nums">{{ formatNumber(count) }}</span>
          </div>
          <div class="flex justify-between items-baseline">
            <span class="text-xs text-gray-500">Share</span>
            <span class="text-sm font-semibold tabular-nums">{{ getPercentage(count) }}%</span>
          </div>
        </div>
      </div>
    </template>
  </UPopover>
</template>
